import React, { useState } from "react";
import {
    Alert, Avatar, Box, Button, Chip, IconButton, LinearProgress, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Tooltip, Typography,
} from "@mui/material";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { MascaraCpf } from "../../../utils/MascaraCpf";
import { camposPendentesSocio, formatarPercentual, somaParticipacoes } from "../../../utils/FichaCadastral";
import ModalSocio from "./ModalSocio";
import ModalExcluirSocio from "./ModalExcluirSocio";
import { botaoPrincipalSx, cardSx, iconeSecaoSx, subtituloSecaoSx, tituloSecaoSx } from "./EstilosFicha";

const cabecalhoSx = { fontSize: 11, fontWeight: 700, letterSpacing: 0.6, color: "#6b7280", textTransform: "uppercase", borderBottom: "none", py: 1 };
const celulaSx = { fontSize: 13, color: "#374151", borderBottom: "1px solid #f3f4f6", py: 1.25 };

const iniciais = (texto) =>
    String(texto ?? "").trim().split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0].toUpperCase()).join("");

// Etapa 2 da ficha: lista, adiciona, edita e exclui sócios
const SociosFicha = ({ socios, onAtualizar, onSucesso, onErro, onAvancar }) => {
    const [openModalSocio, setOpenModalSocio] = useState(false);
    const [openModalExcluir, setOpenModalExcluir] = useState(false);
    // null no modal = novo sócio
    const [socioSelecionado, setSocioSelecionado] = useState(null);

    const soma = somaParticipacoes(socios);
    const disponivel = Math.max(0, Math.round((100 - soma) * 100) / 100);
    const disponivelPara = (socio) => Math.round((disponivel + (Number(socio?.percentual) || 0)) * 100) / 100;

    const abrirCadastro = () => {
        setSocioSelecionado(null);
        setOpenModalSocio(true);
    };

    const abrirEdicao = (socio) => {
        setSocioSelecionado(socio);
        setOpenModalSocio(true);
    };

    const abrirExclusao = (socio) => {
        setSocioSelecionado(socio);
        setOpenModalExcluir(true);
    };

    const handleSuccess = async (texto) => {
        setOpenModalSocio(false);
        setOpenModalExcluir(false);
        await onAtualizar();
        onSucesso(texto);
    };

    const handleError = (texto) => {
        setOpenModalExcluir(false);
        onErro(texto);
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={cardSx}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, flexWrap: "wrap", mb: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box sx={iconeSecaoSx}><GroupsOutlinedIcon /></Box>
                        <Box>
                            <Typography sx={tituloSecaoSx}>Sócios da empresa</Typography>
                            <Typography sx={subtituloSecaoSx}>
                                {socios.length} {socios.length === 1 ? "sócio cadastrado" : "sócios cadastrados"}
                            </Typography>
                        </Box>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<PersonAddAltOutlinedIcon />}
                        onClick={abrirCadastro}
                        disabled={disponivel <= 0}
                        sx={botaoPrincipalSx}
                    >
                        Adicionar sócio
                    </Button>
                </Box>

                {/* Distribuição das participações */}
                <Box sx={{ mb: 2, backgroundColor: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: "10px", p: 1.5 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>Participação distribuída</Typography>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: soma === 100 ? "#15803d" : "#b45309" }}>
                            {formatarPercentual(soma)} de 100%{soma !== 100 ? ` • faltam ${formatarPercentual(disponivel)}` : ""}
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={Math.min(100, soma)}
                        sx={{ mt: 1, height: 6, borderRadius: 3, backgroundColor: "#e5e7eb", "& .MuiLinearProgress-bar": { backgroundColor: soma === 100 ? "#16a34a" : "#0b243d", borderRadius: 3 } }}
                    />
                </Box>

                <TableContainer sx={{ overflowX: "auto" }}>
                    <Table sx={{ minWidth: 620 }}>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: "#f3f4f6", "& th:first-of-type": { borderRadius: "6px 0 0 6px" }, "& th:last-of-type": { borderRadius: "0 6px 6px 0" } }}>
                                <TableCell sx={cabecalhoSx}>Sócio</TableCell>
                                <TableCell sx={cabecalhoSx}>CPF</TableCell>
                                <TableCell sx={cabecalhoSx} align="center">Participação</TableCell>
                                <TableCell sx={cabecalhoSx}>Cadastro</TableCell>
                                <TableCell sx={{ ...cabecalhoSx, width: 110 }} align="center">Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {socios.length > 0 ? socios.map((socio) => {
                                const pendentes = camposPendentesSocio(socio);
                                return (
                                    <TableRow key={socio.idSocio} hover>
                                        <TableCell sx={celulaSx}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                                                <Avatar variant="rounded" sx={{ width: 30, height: 30, fontSize: 11, fontWeight: 700, color: "#ffc845", backgroundColor: "#0b243d", borderRadius: "6px" }}>
                                                    {iniciais(socio.nome)}
                                                </Avatar>
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography noWrap sx={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>{socio.nome}</Typography>
                                                    {socio.email && <Typography noWrap sx={{ fontSize: 11, color: "#6b7280" }}>{socio.email}</Typography>}
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={celulaSx}>{MascaraCpf(socio.cpf)}</TableCell>
                                        <TableCell sx={{ ...celulaSx, fontWeight: 700, color: "#111827" }} align="center">{formatarPercentual(socio.percentual)}</TableCell>
                                        <TableCell sx={celulaSx}>
                                            {pendentes.length === 0 ? (
                                                <Chip label="Completo" size="small" sx={{ backgroundColor: "#dcfce7", color: "#15803d", fontWeight: 600, fontSize: 11, height: 20 }} />
                                            ) : (
                                                <Tooltip title={`Falta: ${pendentes.map((p) => p.nome).join(", ")}`}>
                                                    <Chip label={`${pendentes.length} ${pendentes.length === 1 ? "campo pendente" : "campos pendentes"}`} size="small" sx={{ backgroundColor: "#fef3c7", color: "#b45309", fontWeight: 600, fontSize: 11, height: 20 }} />
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                        <TableCell sx={celulaSx} align="center">
                                            <Tooltip title="Editar">
                                                <IconButton size="small" aria-label={`Editar ${socio.nome}`} onClick={() => abrirEdicao(socio)} sx={{ color: "#374151" }}>
                                                    <EditOutlinedIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Excluir">
                                                <IconButton size="small" aria-label={`Excluir ${socio.nome}`} onClick={() => abrirExclusao(socio)} sx={{ color: "#dc2626" }}>
                                                    <DeleteOutlineIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                            }) : (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ borderBottom: "none", py: 6 }}>
                                        <GroupsOutlinedIcon sx={{ fontSize: 36, color: "#d1d5db" }} />
                                        <Typography sx={{ mt: 1, fontSize: 14, fontWeight: 600, color: "#374151" }}>
                                            Nenhum sócio cadastrado.
                                        </Typography>
                                        <Typography sx={{ fontSize: 12, color: "#6b7280" }}>
                                            Adicione todos os sócios da empresa, inclusive você.
                                        </Typography>
                                        <Button variant="contained" startIcon={<PersonAddAltOutlinedIcon />} onClick={abrirCadastro} sx={{ ...botaoPrincipalSx, mt: 2 }}>
                                            Adicionar sócio
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {socios.length > 0 && soma !== 100 && (
                <Alert severity="warning" sx={{ borderRadius: "8px", fontSize: 13 }}>
                    As participações dos sócios precisam somar 100% para concluir a ficha.
                </Alert>
            )}

            {socios.length > 0 && (
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button variant="contained" endIcon={<ArrowForwardIcon />} onClick={onAvancar} sx={botaoPrincipalSx}>
                        Continuar para documentos
                    </Button>
                </Box>
            )}

            <ModalSocio
                open={openModalSocio}
                socio={socioSelecionado}
                percentualDisponivel={disponivelPara(socioSelecionado)}
                onClose={() => setOpenModalSocio(false)}
                onSuccess={handleSuccess}
            />
            <ModalExcluirSocio
                open={openModalExcluir}
                socio={socioSelecionado}
                onClose={() => setOpenModalExcluir(false)}
                onSuccess={handleSuccess}
                onError={handleError}
            />
        </Box>
    );
};

export default SociosFicha;
